import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Compass, GraduationCap, ArrowUpRight, Star } from "lucide-react";
import { BoardLink } from "../lib/boardLinks";

/**
 * /about — Sovereign Blueprint · EIGOO INC.
 *
 * Director-authored blueprint per directive 2026.02. Pure content
 * page; no API calls. Sovereign Aesthetic baseline applied
 * (deep space canvas, Montserrat H1, gold/neon tactical accents,
 * sharp-edged cards with corner ticks).
 */

const PILLARS = [
    {
        order: "01",
        icon: ShieldCheck,
        label: "Sovereign Infrastructure",
        chip: "TechOps",
        accent: "gold",
        body:
            "24/7 autonomous security and high-pop digital platforms. We refit \"Antique Platforms\" into modern powerhouses.",
    },
    {
        order: "02",
        icon: Compass,
        label: "The Commander's Path",
        chip: "Ownership",
        accent: "neon",
        body:
            "A Map to Ownership via our 60/30/10 LAW — 60% Operator · 30% Fleet · 10% Youth.",
    },
    {
        order: "03",
        icon: GraduationCap,
        label: "The Young Sentinels",
        chip: "The Future",
        accent: "gold",
        body:
            "Funding the training of Hawai\u02BBi's youth in cybersecurity and IT.",
    },
];

export default function About() {
    useEffect(() => {
        document.title = "EIGOO · Sovereign Blueprint";
    }, []);

    return (
        <div
            data-testid="about-page"
            className="min-h-screen text-white"
            style={{
                background:
                    "radial-gradient(ellipse at top, #0D1824 0%, #050A0F 55%, #02060A 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            {/* Sentinel ribbon */}
            <div
                className="border-b"
                style={{ borderColor: "#1E3654", background: "rgba(5,10,15,0.7)" }}
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="about-home-link"
                        className="text-[11px] uppercase tracking-[0.3em]"
                        style={{
                            color: "#E8C56C",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                        }}
                    >
                        ◂ Return to Bridge
                    </Link>
                    <div
                        className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em]"
                        style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}
                    >
                        <span
                            className="inline-block w-2 h-2 rounded-full animate-pulse"
                            style={{ background: "#00E5FF", boxShadow: "0 0 10px rgba(0,229,255,0.6)" }}
                        />
                        Sovereign Blueprint · Live
                    </div>
                </div>
            </div>

            {/* ===== ABOUT ===== */}
            <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
                <div
                    data-testid="about-overline"
                    className="text-[11px] md:text-xs uppercase tracking-[0.4em] mb-6"
                    style={{
                        color: "#00E5FF",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    ◢ Sovereign Blueprint · About EIGOO Inc.
                </div>
                <h1
                    data-testid="about-h1"
                    className="text-5xl md:text-6xl lg:text-7xl uppercase font-black leading-[0.95] tracking-tighter"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                >
                    Restore{" "}
                    <span style={{ color: "#E8C56C", textShadow: "0 0 30px rgba(232,197,108,0.35)" }}>
                        Economic Sovereignty
                    </span>{" "}
                    to Hawai&#699;i.
                </h1>
                <p
                    data-testid="about-body"
                    className="mt-8 max-w-3xl text-base md:text-lg leading-relaxed"
                    style={{ color: "#A9BCD2" }}
                >
                    <strong style={{ color: "#F8FAFC", fontWeight: 700 }}>EIGOO INC.</strong>{" "}
                    is a high-fidelity technology and branding armada. We restore{" "}
                    <span style={{ color: "#E8C56C", fontWeight: 600 }}>Economic Sovereignty</span> and{" "}
                    <span style={{ color: "#00E5FF", fontWeight: 600 }}>Technical Excellence</span>{" "}
                    to Hawai&#699;i. We move local businesses from{" "}
                    <em>&ldquo;Renting Territory&rdquo;</em> on clinical platforms to{" "}
                    <strong style={{ color: "#F8FAFC", fontWeight: 700 }}>Owning their Digital Fortresses</strong>.
                </p>
            </header>

            {/* ===== THE THREE PILLARS ===== */}
            <section
                data-testid="about-pillars"
                className="max-w-[1400px] mx-auto px-6 md:px-10 py-12"
            >
                <div
                    className="text-[11px] uppercase tracking-[0.4em] mb-4"
                    style={{
                        color: "#E8C56C",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                    data-testid="pillars-overline"
                >
                    ◣ The Three Pillars of the Fleet
                </div>
                <h2
                    className="text-3xl md:text-4xl uppercase font-bold tracking-tight mb-12 max-w-3xl"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                >
                    Sovereign Infrastructure · Ownership · The Future
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {PILLARS.map((p, idx) => (
                        <PillarCard key={p.order} pillar={p} index={idx} />
                    ))}
                </div>
            </section>

            {/* ===== THE SOVEREIGN STANDARD ===== */}
            <section
                data-testid="about-standard"
                className="max-w-[1400px] mx-auto px-6 md:px-10 py-16"
            >
                <div
                    className="relative p-10 md:p-14 overflow-hidden"
                    style={{
                        background: "linear-gradient(135deg, #0D1824 0%, #132337 100%)",
                        border: "1px solid #E8C56C",
                        boxShadow:
                            "0 0 50px rgba(232,197,108,0.18), inset 0 0 80px rgba(232,197,108,0.04)",
                    }}
                >
                    {/* Corner ticks */}
                    <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
                    <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: "#E8C56C" }} />

                    <div
                        className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] mb-6"
                        style={{
                            color: "#E8C56C",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                        }}
                    >
                        <Star size={14} style={{ color: "#E8C56C" }} />
                        The Sovereign Standard
                    </div>

                    <h3
                        data-testid="standard-headline"
                        className="text-3xl md:text-5xl uppercase font-black leading-tight tracking-tight"
                        style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                    >
                        We do not build{" "}
                        <span style={{ color: "#E8C56C", fontStyle: "italic" }}>
                            &ldquo;Dull Platforms.&rdquo;
                        </span>
                    </h3>
                    <p
                        data-testid="standard-body"
                        className="mt-6 max-w-3xl text-base md:text-lg leading-relaxed"
                        style={{ color: "#A9BCD2" }}
                    >
                        Every signal is armored with{" "}
                        <span style={{ color: "#E8C56C", fontWeight: 600 }}>visionary design</span>{" "}
                        and{" "}
                        <span style={{ color: "#00E5FF", fontWeight: 600 }}>zero-cap efficiency</span>.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <BoardLink
                            data-testid="about-cta-board"
                            className="inline-flex items-center gap-2 px-7 py-3.5 uppercase tracking-[0.22em] text-sm font-bold transition-all duration-300"
                            style={{
                                fontFamily: "'Montserrat', sans-serif",
                                background: "#E8C56C",
                                color: "#050A0F",
                                border: "1px solid #E8C56C",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 0 30px rgba(232,197,108,0.55)";
                                e.currentTarget.style.transform = "translateY(-1px)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.transform = "translateY(0)";
                            }}
                        >
                            [ BOARD THE FLEET ] <ArrowUpRight size={14} />
                        </BoardLink>
                        <Link
                            to="/partners"
                            data-testid="about-cta-partners"
                            className="inline-flex items-center gap-2 px-7 py-3.5 uppercase tracking-[0.22em] text-sm font-bold transition-all duration-300"
                            style={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#00E5FF",
                                border: "1px solid #00E5FF",
                                background: "transparent",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(0,229,255,0.08)";
                                e.currentTarget.style.boxShadow = "0 0 20px rgba(0,229,255,0.45)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            [ PARTNERS HUB ]
                        </Link>
                    </div>
                </div>
            </section>

            <div className="h-16" />
        </div>
    );
}

function PillarCard({ pillar, index }) {
    const Icon = pillar.icon;
    const isGold = pillar.accent === "gold";
    const ring = isGold ? "#E8C56C" : "#00E5FF";
    const glow = isGold ? "rgba(232,197,108,0.45)" : "rgba(0,229,255,0.5)";

    return (
        <article
            data-testid={`pillar-card-${pillar.order}`}
            className="relative flex flex-col p-7 transition-all duration-300"
            style={{
                background: "linear-gradient(180deg, #0D1824 0%, #0A1320 100%)",
                border: "1px solid #1E3654",
                animation: `pillarFadeIn 0.6s ease-out ${index * 0.08}s both`,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = ring;
                e.currentTarget.style.boxShadow = `0 0 30px ${glow}`;
                e.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#1E3654";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <span aria-hidden className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: ring }} />
            <span aria-hidden className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: ring }} />

            <div className="flex items-start justify-between mb-6">
                <span
                    className="font-mono text-xs"
                    style={{ color: ring, letterSpacing: "0.25em" }}
                >
                    PILLAR {pillar.order}
                </span>
                <span
                    className="text-[9px] uppercase tracking-[0.3em] px-2 py-1"
                    style={{
                        color: ring,
                        background: `${isGold ? "rgba(232,197,108,0.08)" : "rgba(0,229,255,0.08)"}`,
                        border: `1px solid ${ring}33`,
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                    }}
                >
                    {pillar.chip}
                </span>
            </div>

            <div
                className="mb-5 inline-flex items-center justify-center w-12 h-12"
                style={{
                    border: `1px solid ${ring}55`,
                    background: `${isGold ? "rgba(232,197,108,0.06)" : "rgba(0,229,255,0.06)"}`,
                }}
            >
                <Icon size={22} style={{ color: ring }} />
            </div>

            <h3
                data-testid={`pillar-name-${pillar.order}`}
                className="text-2xl uppercase font-black leading-tight tracking-tight"
                style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
            >
                {pillar.label}
            </h3>
            <p
                className="mt-4 text-base leading-relaxed flex-1"
                style={{ color: "#A9BCD2" }}
            >
                {pillar.body}
            </p>
        </article>
    );
}

// Inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("about-fade-kf")) {
    const style = document.createElement("style");
    style.id = "about-fade-kf";
    style.textContent = `
@keyframes pillarFadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
}`;
    document.head.appendChild(style);
}
